import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Trash2 } from 'lucide-react';

import { api } from '@/api/client';
import type { ResetOption, ResetPreview, ResetRequest, ResetResult } from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { Page, PageHeader, Panel } from '@/components/layout/Page';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/EmptyState';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';

import styles from './DataResetPage.module.css';

const ENDPOINT = '/api/v1/settings/data-reset';

function ResetDialog({
  option,
  onClose,
  onReset,
}: {
  option: ResetOption;
  onClose: () => void;
  onReset: (result: ResetResult) => void;
}) {
  const [phrase, setPhrase] = useState('');
  const preview = useQuery({
    queryKey: ['data-reset', 'preview', option.module],
    queryFn: () => api.get<ResetPreview>(`${ENDPOINT}/${option.module}/preview`),
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  const reset = useMutation({
    mutationFn: (payload: ResetRequest) =>
      api.post<ResetResult>(`${ENDPOINT}/${option.module}`, payload),
    onSuccess: onReset,
  });
  const data = preview.data;
  const affected = data?.impact[0]?.count ?? 0;
  const ready =
    data && affected > 0 && phrase === data.phrase && !preview.isFetching && !preview.isError;
  function refresh() {
    setPhrase('');
    reset.reset();
    void preview.refetch();
  }
  function close() {
    if (!reset.isPending) onClose();
  }

  return (
    <Modal
      open
      onClose={close}
      title={`Reset ${option.label}?`}
      description={data?.warning ?? option.warning}
      tone="danger"
      icon={<AlertTriangle size={20} aria-hidden="true" />}
      size="md"
      footer={
        <>
          <Button variant="ghost" disabled={reset.isPending} onClick={close}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={reset.isPending}
            disabled={!ready || reset.isError}
            icon={<Trash2 size={16} aria-hidden="true" />}
            onClick={() => {
              if (data && ready) {
                reset.mutate({ phrase, confirmation_token: data.confirmation_token });
              }
            }}
          >
            Permanently reset
          </Button>
        </>
      }
    >
      {preview.isFetching ? (
        <Spinner label="Checking affected records" />
      ) : preview.isError ? (
        <ErrorState message={preview.error.message} onRetry={refresh} />
      ) : data ? (
        <>
          <dl className={styles.impact}>
            {data.impact.map(({ label, count }) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{count.toLocaleString()}</dd>
              </div>
            ))}
          </dl>
          {reset.isError ? (
            <ErrorState message={reset.error.message} onRetry={refresh} />
          ) : affected === 0 ? (
            <p>There are no records to reset in this module.</p>
          ) : (
            <Field
              label="Confirmation phrase"
              hint={
                <>
                  Type <strong>{data.phrase}</strong> exactly as shown. This cannot be undone in
                  the CRM.
                </>
              }
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  describedBy={describedBy}
                  value={phrase}
                  onChange={(event) => {
                    setPhrase(event.target.value);
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  disabled={reset.isPending}
                />
              )}
            </Field>
          )}
        </>
      ) : null}
    </Modal>
  );
}

export function DataResetPage() {
  const client = useQueryClient();
  const [selected, setSelected] = useState<ResetOption | null>(null);
  const [receipt, setReceipt] = useState('');
  const options = useQuery({
    queryKey: ['data-reset', 'options'],
    queryFn: () => api.get<ResetOption[]>(ENDPOINT),
    retry: false,
  });

  return (
    <Page className={styles.page}>
      <PageHeader
        title="Data reset"
        description="Admins and Superadmins with Settings access can reset data one module at a time."
      />
      <div className={styles.sections}>
        <Alert tone="warning">
          <strong>Resets permanently remove CRM records.</strong> Review the affected data and
          make sure you have a current backup before confirming.
        </Alert>
        {receipt ? <Alert tone="success">{receipt}</Alert> : null}
        {options.isPending ? (
          <Spinner label="Loading reset options" />
        ) : options.isError ? (
          <ErrorState
            message={options.error.message}
            onRetry={() => {
              void options.refetch();
            }}
          />
        ) : (
          <Panel title="Reset by module">
            <ul className={styles.options}>
              {options.data.map((option) => (
                <li key={option.module}>
                  <div>
                    <h3>{option.label}</h3>
                    <p>{option.warning}</p>
                  </div>
                  <Button
                    variant="ghost"
                    icon={<Trash2 size={15} aria-hidden="true" />}
                    aria-label={`Review reset for ${option.label}`}
                    onClick={() => {
                      setReceipt('');
                      setSelected(option);
                    }}
                  >
                    Review reset
                  </Button>
                </li>
              ))}
            </ul>
          </Panel>
        )}
      </div>
      {selected ? (
        <ResetDialog
          key={selected.module}
          option={selected}
          onClose={() => {
            setSelected(null);
          }}
          onReset={({ removed }) => {
            setReceipt(
              `${selected.label} reset complete. ${removed.toLocaleString()} ${removed === 1 ? 'record' : 'records'} removed, along with linked data listed in the preview.`,
            );
            setSelected(null);
            void client.invalidateQueries();
          }}
        />
      ) : null}
    </Page>
  );
}
